import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Hammer, FileSpreadsheet } from 'lucide-react';
import { getStoreDefaults, getStorePaceAccount, updateStore } from '../../../utils/queries';
import ModeCard from '../../../components/ModeCard';

// Every field here is populated by the Setup page's Connect to ERP flow
// (see paceOnboarding.js's applyPaceCustomerDefaults) from a live ERP
// lookup -- there's nothing for an admin to type in, and editing them here
// independently of ERP is how the ERP Customer Number field on the
// General tab drifted out of sync in the first place. Read-only reference
// data only; Connect/reconnect happens on Setup.
const FIELDS = [
	{ key: 'erpCsrId', label: 'CSR ID' },
	{ key: 'erpSalespersonId', label: 'Salesperson ID' },
	{ key: 'erpJobTypeId', label: 'Job Type ID' },
	{ key: 'erpManufacturingLocationId', label: 'Manufacturing Location ID' },
];

export default function DefaultsTab({ store, onUpdated }) {
	const navigate = useNavigate();
	const [defaults, setDefaults] = useState(null);
	const [paceAccount, setPaceAccount] = useState(null);
	const [loading, setLoading] = useState(true);
	const [jobCreationMethod, setJobCreationMethod] = useState(store.jobCreationMethod || 'estimate_based');
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');
	const [savedMsg, setSavedMsg] = useState('');

	useEffect(() => {
		setLoading(true);
		Promise.all([getStoreDefaults(store.storeId), getStorePaceAccount(store.storeId)]).then(
			([defaultsResult, paceAccountResult]) => {
				setDefaults(defaultsResult?.success ? defaultsResult.data : null);
				setPaceAccount(paceAccountResult?.success ? paceAccountResult.data : null);
				setLoading(false);
			}
		);
	}, [store.storeId]);

	const handleJobCreationMethodChange = async (nextValue) => {
		setJobCreationMethod(nextValue);
		setSaving(true);
		setError('');
		setSavedMsg('');
		const result = await updateStore(store.storeId, { jobCreationMethod: nextValue });
		setSaving(false);
		if (!result?.success) {
			setJobCreationMethod(store.jobCreationMethod || 'estimate_based');
			setError(result?.error?.message || 'Failed to save.');
			return;
		}
		setSavedMsg('Saved.');
		onUpdated?.(result.data);
	};

	if (loading) {
		return <p className='text-sm text-gray-400'>Loading...</p>;
	}

	return (
		<div className='max-w-3xl space-y-4'>
			<div>
				<label>ERP Customer Number</label>
				<div className='flex items-center gap-3'>
					{paceAccount?.paceCustomerNumber ? (
						<span className='inline-flex items-center text-xs bg-gray-100 rounded-full px-2.5 py-1'>
							{paceAccount.paceCustomerNumber}
						</span>
					) : (
						<span className='inline-flex items-center text-xs text-yellow-800 bg-yellow-100 rounded-full px-2.5 py-1'>
							Not connected
						</span>
					)}
					<button type='button' className='btn-secondary-small shrink-0' onClick={() => navigate('/settings/setup')}>
						{paceAccount?.paceCustomerNumber ? 'Manage' : 'Connect to ERP'}
					</button>
				</div>
				<p className='text-xs text-gray-400 mt-1'>
					Set on the Store Setup page -- connecting looks the customer up in ERP and fills in the fields below.
				</p>
			</div>

			{FIELDS.map((f) => (
				<div key={f.key} className='max-w-60'>
					<label>{f.label}</label>
					<input className='form-input bg-gray-100' value={defaults?.[f.key] || ''} placeholder='—' disabled />
				</div>
			))}

			<div className='pt-4'>
				<label>ERP Job Creation Method</label>
				<p className='text-xs text-gray-400 mb-1'>How this store's orders are submitted to ERP as Jobs.</p>
				{error && <div className='text-sm text-pi-red mb-1'>{error}</div>}
				{savedMsg && <div className='text-sm text-green-700 mb-1'>{savedMsg}</div>}
				<div className='grid grid-cols-2 gap-4 mt-1'>
					<ModeCard
						selected={jobCreationMethod === 'estimate_based'}
						current={jobCreationMethod === 'estimate_based'}
						onClick={() => !saving && handleJobCreationMethodChange('estimate_based')}
						icon={<FileSpreadsheet className='size-4 text-gray-600' />}
						label='Estimate Based'
						description='Convert a pre-built ERP Estimate into a Job. The standard pattern for dollar-priced storefronts.'
						disabled={saving}
					/>
					<ModeCard
						selected={jobCreationMethod !== 'estimate_based'}
						current={jobCreationMethod !== 'estimate_based'}
						onClick={() => !saving && handleJobCreationMethodChange('non_estimate_based')}
						icon={<Hammer className='size-4 text-gray-600' />}
						label='Non-Estimate Based'
						description='Build the ERP Job directly, line by line. For stores with no ERP Estimates, e.g. loyalty-points redemption.'
						disabled={saving}
					/>
				</div>
			</div>
		</div>
	);
}
